import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Collapse,
  LinearProgress,
  Grid,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Flag as FlagIcon
} from '@mui/icons-material';

interface StrategicGoal {
  id: string;
  title: string;
  progress: number;
  target: string;
}

interface MissionVisionBannerProps {
  mission?: string;
  vision?: string;
  quarterFocus?: string;
  goals?: StrategicGoal[];
  defaultExpanded?: boolean;
}

const MissionVisionBanner: React.FC<MissionVisionBannerProps> = ({
  mission = "Transform federal data management through AI-powered governance and unified metadata",
  vision = "Become the trusted platform for DHS data excellence and innovation",
  quarterFocus = "Q4 2025: Cloud Migration & AI Integration",
  goals = [
    { id: '1', title: 'Complete Cloud Migration', progress: 40, target: '75%' },
    { id: '2', title: 'Enhance Data Quality', progress: 65, target: '90%' },
    { id: '3', title: 'User Adoption Growth', progress: 55, target: '85%' }
  ],
  defaultExpanded = true
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const getProgressColor = (progress: number): string => {
    if (progress >= 75) return '#4caf50'; // Green
    if (progress >= 50) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#003366',
        color: 'white',
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden'
      }}
      role="region"
      aria-label="Mission and Vision"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls="mission-vision-content"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FlagIcon sx={{ fontSize: 28, color: 'white' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            Mission & Strategic Vision
          </Typography>
        </Box>
        <IconButton
          size="small"
          sx={{ color: 'white' }}
          aria-label={expanded ? 'Collapse mission and vision' : 'Expand mission and vision'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          id="mission-vision-content"
          sx={{ px: 3, pb: 3 }}
        >
          <Grid container spacing={3}>
            {/* Mission */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    letterSpacing: 1.2
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    mt: 1,
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}
                >
                  {mission}
                </Typography>
              </Box>
            </Grid>

            {/* Vision */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    letterSpacing: 1.2
                  }}
                >
                  Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'white',
                    mt: 1,
                    fontSize: '1rem',
                    lineHeight: 1.6
                  }}
                >
                  {vision}
                </Typography>
              </Box>
            </Grid>

            {/* Current Quarter Focus */}
            <Grid item xs={12}>
              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 1,
                  p: 2,
                  mt: 1
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    letterSpacing: 1.2
                  }}
                >
                  Current Focus
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    mt: 0.5,
                    mb: 2,
                    fontWeight: 500
                  }}
                >
                  {quarterFocus}
                </Typography>

                {/* Strategic Goals */}
                <Grid container spacing={2}>
                  {goals.map((goal) => (
                    <Grid item xs={12} sm={4} key={goal.id}>
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.875rem'
                            }}
                          >
                            {goal.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.8)',
                              fontWeight: 600
                            }}
                          >
                            {goal.progress}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={goal.progress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getProgressColor(goal.progress),
                              borderRadius: 3
                            }
                          }}
                          aria-label={`${goal.title} progress: ${goal.progress}%`}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: '0.75rem',
                            mt: 0.5,
                            display: 'block'
                          }}
                        >
                          Target: {goal.target}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MissionVisionBanner;
